import React, { Component, FormEvent, ChangeEvent, FocusEvent, useContext, useEffect } from 'react';
import { withRouter, useHistory, useLocation } from 'react-router-dom';
import classnames from 'classnames';
import {
  Button,
  Card,
  CardHeader,
  Form,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Input,
  CardBody,
  Container,
  Row,
  Col,
} from 'reactstrap';
import { SignUpLink } from '../SignUp/SignUp';
import { PasswordForgetLink } from '../PasswordForget/PasswordForget';
import { withFirebase } from '../../components/Firebase';
import * as ROUTES from '../../constants/routes';
import { AuthUserContext } from '../../components/Session';
import { safeMigration } from '../../config/featureFlags';

const SignInPage = () => {
  const authUser = useContext(AuthUserContext) as any;
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    // Wait for auth
    if (!authUser) return;

    // Honour ?next= (set by withAuthorization when an unauthenticated user lands
    // on a protected route, or by email CTAs that deep-link into the app).
    // Only accept same-origin paths starting with '/' (and not '//') to block
    // open-redirect attacks.
    const search = new URLSearchParams(location.search);
    const next = search.get('next');
    if (next && next.startsWith('/') && !next.startsWith('//') && next !== ROUTES.SIGN_IN) {
      history.replace(next);
      return;
    }

    // Check if there's a pending invite code from the invite flow
    const pendingInviteCode = sessionStorage.getItem('pendingInviteCode');
    if (pendingInviteCode) {
      // Clear the stored code and redirect back to the invite page
      sessionStorage.removeItem('pendingInviteCode');
      history.push(`/invite/${pendingInviteCode}`);
      return;
    }

    // Check if there's a pending claim code from the license claim flow
    const pendingClaimCode = sessionStorage.getItem('pendingClaimCode');
    if (pendingClaimCode) {
      sessionStorage.removeItem('pendingClaimCode');
      history.push(`/claim/${pendingClaimCode}`);
      return;
    }

    // Check if there's a pending review code from the spot review flow
    const pendingReviewCode = sessionStorage.getItem('pendingReviewCode');
    if (pendingReviewCode) {
      sessionStorage.removeItem('pendingReviewCode');
      history.push(`/review/${pendingReviewCode}`);
      return;
    }

    // Check if there's a pending delivery code from the broadcaster delivery flow
    const pendingDeliveryCode = sessionStorage.getItem('pendingDeliveryCode');
    if (pendingDeliveryCode) {
      sessionStorage.removeItem('pendingDeliveryCode');
      history.push(`/delivery/${pendingDeliveryCode}`);
      return;
    }

    // Get orgs directly from authUser context (more reliable than store during login)
    const userOrgs = authUser?.organizations || [];

    // If user has no organizations, redirect to get-started
    if (userOrgs.length === 0) {
      history.push(ROUTES.GET_STARTED);
    } else if (userOrgs.length === 1) {
      // User has exactly one org, select it automatically and go to dashboard
      history.push(ROUTES.HOME);
    } else {
      // User has multiple orgs, let them choose which one to use
      // Pass fromSignIn flag so SelectOrganization knows not to show Back button
      history.push(ROUTES.SELECT_ORGANIZATION, { fromSignIn: true });
    }
  }, [authUser, history]);

  return (
    <Container>
      <div className="wrapper">
        <Row>
          <Col className="mx-auto" lg="5" md="12">
            <div className="text-center mb-4">
              <strong
                className="font-weight-bold text-light"
                style={{ fontSize: '1.5rem', letterSpacing: '0.1em' }}
              >
                RADIO VAULT
              </strong>
            </div>
            <Card className="text-center modal-style">
              <CardHeader>
                <h4 className="modal-title">Sign In</h4>
              </CardHeader>
              <SignInForm />
            </Card>
          </Col>
        </Row>
      </div>
    </Container>
  );
};

const INITIAL_STATE = {
  email: '',
  password: '',
  error: null,
  emailFocus: false,
  passwordFocus: false,
};

type SignUpFormState = {
  email: string;
  password: string;
  emailFocus: boolean;
  passwordFocus: boolean;
  error: any;
};

type SignUpFormProps = {
  firebase: any;
  history: any;
};

class SignInFormBase extends Component<SignUpFormProps, SignUpFormState> {
  constructor(props: SignUpFormProps) {
    super(props);
    this.state = { ...INITIAL_STATE };
  }
  onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const { email, password } = this.state;
    
    try {
      await safeMigration(
        'useAuthHooks',
        // New hook method - using original for now as this is a class component
        async () => {
          await this.props.firebase.doSignInWithEmailAndPassword(email, password);
        },
        // Original method (fallback)
        async () => {
          await this.props.firebase.doSignInWithEmailAndPassword(email, password);
        },
        'SignIn-doSignInWithEmailAndPassword'
      );
      
      this.setState({ ...INITIAL_STATE });
      // Don't redirect here - let SignInPage's useEffect handle redirect
      // after auth state and organizations are loaded
    } catch (error: any) {
      let errorMessage = 'An error occurred during sign in.';
      if (error.message && error.message.includes('INVALID_LOGIN_CREDENTIALS')) {
        errorMessage = 'Invalid email or password. Please try again.';
      } else if (error.message && error.message.includes('TOO_MANY_ATTEMPTS_TRY_LATER')) {
        errorMessage = 'Too many failed attempts. Please try again later.';
      } else if (error.message && error.message.includes('USER_DISABLED')) {
        errorMessage = 'This account has been disabled. Please contact support.';
      }
      this.setState({ error: { message: errorMessage } });
    }
  };
  onChange = (event: ChangeEvent<HTMLInputElement>) => {
    this.setState({ [event.target.name]: event.target.value } as any);
  };
  render() {
    const { email, password, error, emailFocus, passwordFocus } = this.state;
    const isInvalid = password === '' || email === '';
    return (
      <>
        <Form autoComplete="off" className="form" onSubmit={this.onSubmit}>
          <CardBody>
            <InputGroup
              className={classnames({
                'input-group-focus': emailFocus,
              })}
            >
              <InputGroupAddon addonType="prepend">
                <InputGroupText>
                  <i className="tim-icons icon-email-85" />
                </InputGroupText>
              </InputGroupAddon>
              <Input
                placeholder="Email"
                name="email"
                type="text"
                value={email}
                onChange={this.onChange}
                onFocus={(event: FocusEvent<HTMLInputElement>) => this.setState({ emailFocus: true })}
                onBlur={(event: FocusEvent<HTMLInputElement>) => this.setState({ emailFocus: false })}
              />
            </InputGroup>
            <InputGroup
              className={classnames({
                'input-group-focus': passwordFocus,
              })}
            >
              <InputGroupAddon addonType="prepend">
                <InputGroupText>
                  <i className="tim-icons  icon-lock-circle" />
                </InputGroupText>
              </InputGroupAddon>
              <Input
                placeholder="Password"
                name="password"
                type="password"
                value={password}
                onChange={this.onChange}
                onFocus={(event: FocusEvent<HTMLInputElement>) => this.setState({ passwordFocus: true })}
                onBlur={(event: FocusEvent<HTMLInputElement>) => this.setState({ passwordFocus: false })}
              />
            </InputGroup>
            <Button
              disabled={isInvalid}
              className="btn-info-custom"
              size="lg"
              type="submit"
            >
              <span>Continue</span>
            </Button>
            {error && <p className="text-warning pt-2">{error.message}</p>}
          </CardBody>
          <div className="card-footer">
            <SignUpLink />
            <PasswordForgetLink />
          </div>
        </Form>
      </>
    );
  }
}
const SignInForm = withRouter(withFirebase(SignInFormBase));

export default SignInPage;
export { SignInForm };
